import React, { useEffect, useRef, useState } from 'react';
import apis from '@src/apis';
import moment from 'moment';
import logo from '@src/assets/logo/logo-AiVoice-new.svg';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import theme from '@src/styles/theme';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import { STATUS_SURVEY } from '@src/constants/survey';
import { StyledSurvey } from './index.style';
import Question from './Question';
import StepProgressBar from './StepProgressBar';
import ActionsButton from './ActionsButton';

const Survey = ({ closeUserSurvey, steps }) => {
  const { t, i18n } = useTranslation();
  const { height } = useWindowDimensions();
  const { language } = i18n;
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState({});
  const [textOtherOption, setTextOtherOption] = useState({});
  const [userSelectAnswer, setUserSelectAnswer] = useState(steps || {});
  const surveyRef = useRef(null);
  const startTime = useRef(moment());
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const optionType = currentStep?.multiSelect
    ? t('chooseMultiple')
    : t('chooseOneOption');
  const currentAnswers = userSelectAnswer[currentStep?.key] || {};
  const hasInputOther = currentAnswers?.selectedOptions?.some(
    (e) => e.isCustom,
  );

  const indexQuestion = questions?.findIndex((q) => q.key === currentStep?.key);
  const hasOtherAnswer = currentAnswers?.selectedOptions?.some(
    (answer) => answer.isCustom,
  );
  const hasOtherText = Boolean(textOtherOption[currentStep?.key]);
  const hasButtonNextDisabled =
    !currentAnswers?.selectedOptions?.length ||
    (hasOtherAnswer && !hasOtherText);
  const hasButtonPreviousDisabled = indexQuestion <= 0;
  const hasButtonFinish = indexQuestion === questions.length - 1;

  const handleChangeOtherText = (text) => {
    const selected = { ...textOtherOption };
    selected[currentStep?.key] = text;
    setTextOtherOption(selected);
  };

  const selectAnswer = (currentSteps, multiSelect) => (answerSelected) => {
    const { active, icon, ...rest } = answerSelected || {};
    setUserSelectAnswer((prev) => {
      const answers = (prev[currentSteps?.key] || {})?.selectedOptions || [];
      const itemIndex = answers.findIndex((ans) => ans.key === rest.key);

      let updatedAnswers;
      if (itemIndex !== -1) {
        if (answers?.length === 1) {
          updatedAnswers = answers;
        } else {
          updatedAnswers = answers.filter((ans) => ans.key !== rest.key);
        }
      } else {
        updatedAnswers = multiSelect ? [...answers, rest] : [rest];
      }
      if (!currentSteps?.key) {
        return prev;
      }

      return {
        ...prev,
        [currentSteps?.key]: {
          key: currentSteps?.key,
          order: currentSteps?.order,
          selectedOptions: updatedAnswers,
        },
      };
    });
  };
  const buildUpdatedAnswers = (answers, others) =>
    Object.entries(answers).reduce((acc, [key, answer]) => {
      const updatedOptions = answer?.selectedOptions?.map((option) =>
        option.isCustom
          ? { ...option, customValue: others[key] }
          : { ...option },
      );
      if (updatedOptions?.length > 0) {
        acc[key] = {
          ...answer,
          selectedOptions: updatedOptions || [],
        };
        return acc;
      }
      return acc;
    }, {});
  const handleUpdateSurvey = async (status) => {
    const questionKey = currentStep?.key;
    if (!questionKey) {
      return;
    }

    const now = moment();
    const duration = now.diff(startTime.current, 'milliseconds');
    if (!userSelectAnswer[questionKey]) {
      userSelectAnswer[questionKey] = {};
    }
    const updatedAnswers = buildUpdatedAnswers(
      userSelectAnswer,
      textOtherOption,
    );

    const currentAnswer = {
      ...updatedAnswers[questionKey],
      duration,
    };

    const updatedParams = {
      ...updatedAnswers,
      ...(currentAnswer?.selectedOptions?.length > 0
        ? { [questionKey]: currentAnswer }
        : {}),
    };

    setUserSelectAnswer(updatedParams);

    await apis.survey.updateSurvey({
      status,
      steps: updatedParams,
    });
  };
  const onContinue = () => {
    if (!hasButtonFinish) {
      handleUpdateSurvey(STATUS_SURVEY.DOING);
      setCurrentStep(questions[indexQuestion + 1]);
      startTime.current = moment();
      surveyRef?.current.scrollIntoView();
    } else {
      handleUpdateSurvey(STATUS_SURVEY.DONE);
      closeUserSurvey(STATUS_SURVEY.DONE);
    }
  };

  const onPrevious = () => {
    handleUpdateSurvey(STATUS_SURVEY.DOING);
    startTime.current = moment();
    if (indexQuestion > 0) {
      setCurrentStep(questions[indexQuestion - 1]);
    }
  };

  const onSkip = () => {
    handleUpdateSurvey(STATUS_SURVEY.SKIPPED);
    closeUserSurvey(STATUS_SURVEY.SKIPPED);
  };

  const getLastestOrderKey = (currentSteps) =>
    Object.keys(currentSteps).reduce((maxKey, currentKey) => {
      const maxOrder = currentSteps?.[maxKey]?.order || 0;
      const currentOrder = currentSteps?.[currentKey]?.order || 0;
      return maxOrder > currentOrder ? maxKey : currentKey;
    }, '');

  const parseOtherStep = (currentSteps) =>
    Object.fromEntries(
      Object.entries(currentSteps).map(([key, value]) => {
        const customValue = value?.selectedOptions?.find(
          (e) => e.customValue,
        )?.customValue;
        return [key, customValue || ''];
      }),
    );

  useEffect(() => {
    const getSurvey = async () => {
      const response = await apis.survey.getSurveys();
      if (response?.result?.active) {
        const surveySteps = response?.result?.steps || [];
        setQuestions(surveySteps);
      }
    };
    getSurvey();
  }, []);
  useEffect(() => {
    const updateCurrentStepSurvey = async () => {
      const surveySteps = questions || [];
      if (steps) {
        const maxKey = getLastestOrderKey(steps);
        const nextIndex = surveySteps.findIndex((e) => e.key === maxKey);
        let nextQuestion = surveySteps[nextIndex + 1];
        if (nextIndex === questions?.length - 1) {
          nextQuestion = surveySteps[nextIndex];
        }

        setCurrentStep(nextQuestion);
        setTextOtherOption(parseOtherStep(steps));
        setUserSelectAnswer(steps);
      } else {
        setCurrentStep(surveySteps[0]);
      }
    };
    if (questions?.length) updateCurrentStepSurvey();
  }, [steps, questions]);

  return (
    <StyledSurvey isMobile={isMobile} height={height}>
      <div ref={surveyRef} />
      <div className="container-survey">
        {!isMobile && <img src={logo} alt="Logo" style={{ width: '100px' }} />}
        <StepProgressBar
          hasButtonPreviousDisabled={hasButtonPreviousDisabled}
          amountStep={questions?.length || 0}
          activeStep={indexQuestion}
          onPrevious={onPrevious}
          onSkip={onSkip}
        />
        <Question
          titleQuestion={currentStep?.title?.[language || 'vi']}
          subOption={optionType}
          answers={currentStep?.options}
          answersSelected={currentAnswers?.selectedOptions || []}
          onSelected={selectAnswer(currentStep, currentStep?.multiSelect)}
          valueTextOther={textOtherOption[currentStep?.key]}
          handleChangeTextOther={handleChangeOtherText}
          hasInputOther={hasInputOther}
        />
        <ActionsButton
          onSkip={onSkip}
          onPrevious={onPrevious}
          onContinue={onContinue}
          hasButtonNextDisabled={hasButtonNextDisabled}
          hasButtonPreviousDisabled={hasButtonPreviousDisabled}
          hasButtonFinish={hasButtonFinish}
        />
      </div>
    </StyledSurvey>
  );
};

export default Survey;
